import { Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ChoosenPositions, Companies, Personen, Requests, Staff, WebserviceResponse } from "../../../_shared/interfaces";
import {
  AlertService, IAlertType,
  PersonsStoreService,
  RequestsStoreService,
  StaffStoreService
} from "../../../_shared/services";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import { Router, RouterLink} from "@angular/router";
import { DatePipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import { Subject} from "rxjs";
import { ClarityIcons, twoWayArrowsIcon} from "@cds/core/icon";
import {
  ClrDatagridModule,
  ClrDatepickerModule,
  ClrInputModule,
  ClrRadioModule,
  ClrSelectModule,
  ClrStepperModule
} from "@clr/angular";
import { RequestCompanySearchComponent} from "../request-company-search/request-company-search.component";
import { getTenantStorage } from '../../../_shared/utils/tenant-context';
import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';

import { environment as env } from '../../../../environments/environment';
import {TranslocoDirective} from "@jsverse/transloco";
const staffId: number = parseInt(getTenantStorage('staff_id') ?? getTenantStorage('user_id') ?? '0', 10);

import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-request-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    ClrStepperModule,
    ReactiveFormsModule,
    ClrInputModule,
    ClrDatagridModule,
    ClrSelectModule,
    ClrDatepickerModule,
    NgIf,
    RouterLink,
    ClrRadioModule,
    NgForOf,
    JsonPipe,
    RequestCompanySearchComponent,
    DatePipe,
    TranslocoDirective
  ],
  templateUrl: './request-form.component.html',
  styleUrl: './request-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})

export class RequestFormComponent implements OnInit, OnChanges {
  requestForm!: FormGroup;
  private defaultDate: Date = new Date();
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.requestForm);
  }

  @Input() choosen: ChoosenPositions[] = [];
  @Input() companyId: number = 0;
  @Input() projectId: string = '';
  @Input() requestId: number = 0;
  @Input() staffId: number = 0;
  @Input() isUpdateMode = false;
  @Input() requestsData?: Requests;
  @Output() formSubmit = new EventEmitter<Request>();

  devMode: boolean = false;
  crmMode: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private personsService: PersonsStoreService,
    private reqss: RequestsStoreService,
    private staffService: StaffStoreService,
    private fb: FormBuilder,
    private router: Router,
    private alertDispatcher: AlertService,
    private datePipe: DatePipe
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.crmMode = env.externalCrm;
    this.initForm();
  }

  ngOnInit() {
    this.loadStaff();
    // this.companyId = parseInt(this.splitProjectNumber(this.projectId, 0));
    // this.loadPersons(this.requestsData?.requests_cid || 0 );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['requestsData'] && this.requestsData) {
      this.patchFormValues(this.requestsData);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.requestForm = this.fb.group({
      stepI: this.fb.group({
        project_id: this.companyId + '.' + this.projectId,
        reqcid: [''],
        company_name: [''],
        reqstreet: [''],
        reqpostcode: [''],
        reqcity: [''],
        reqcountry: [''],
        reqstatus: [''],
        reqsubject: ['', [Validators.required, Validators.maxLength(250)]],
        purchasing_id: [''],
        reqdate: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        reqsubmit: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
      }),
      stepII: this.fb.group({
        employee_phm: staffId,
      }),
      stepIII: this.fb.group({
        employee_req: 0,
      })
    });
  }

  preFillRow(supplier: Companies) {
    this.requestForm = this.fb.group({
      selected_list_assembly: [this.choosen],
      stepI: this.fb.group({
        project_id: this.companyId + '.' + this.projectId,
        reqcid: supplier.cid,
        company_name: supplier.company,
        reqstreet: supplier.street,
        reqpostcode: supplier.postcode,
        reqcity: supplier.city,
        reqcountry: this.mapCountryForForm(supplier.country),
        reqstatus: ['open'],
        reqsubject: ['', [Validators.required, Validators.maxLength(250)]],
        purchasing_id: [''],
        reqdate: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        reqsubmit: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
      }),
      stepII: this.fb.group({
        employee_phm: staffId,
      }),
      stepIII: this.fb.group({
        employee_req: 0,
      })
    });

    this.loadPersons(supplier.cid, true)
  }

  private patchFormValues(data: Requests) {
    if (!data) return;

    const formValue = {
      stepI: {
        project_id: data.project_id || '',
        reqcid: data.requests_cid || 0,
        company_name: data.requests_company || '',
        reqstreet: data.reqstreet || '',
        reqpostcode: data.reqpostcode || '',
        reqcity: data.reqcity || '',
        reqcountry: this.mapCountryForForm(data.reqcountry || ''),
        reqsubject: data.subject || '',
        purchasing_id: data.purchasing_id || 0,
        reqdate: this.datePipe.transform(data.requests_date, 'dd.MM.yyyy'),
        reqsubmit: this.datePipe.transform(data.requests_submit, 'dd.MM.yyyy'),
        reqstatus: data.status || '',
      },
      stepII: {
        employee_phm: data.employee_phm || staffId,
      },
      stepIII: {
        employee_req: data.requests_person || 0,
      }
    };
    this.requestForm.patchValue(formValue);
    this.loadPersons(data.requests_cid, this.isUpdateMode ? undefined : true)
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  projectLeader(): number {
    return this.requestForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  createRequest() {
    this.reqss.createRequest(this.requestForm.value).subscribe({
      next: (response) => {
        this.requestId = parseInt(response.message);

        this.alertDispatcher.alertConfig.message = 'Eine neue Anfrage wurde angelegt!';
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;

        this.router.navigate(['request/details' , this.companyId + '.' + this.projectId, this.requestId]);

      },
      complete: () => {
        if (this.devMode) console.log('196 - ' + this.requestId)
      },
      error: (error) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Erstellen des Requests: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  updateRequest(requestId: number) {
    if (this.requestForm.valid) {
      const formValue = this.requestForm.value;
      this.reqss.updateRequest(formValue, requestId).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Request erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(formValue);
          this.router.navigate(['request/details' , this.projectId, requestId]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren des Requests: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }

  disableStepII() {
    let disable: boolean = false;
    if (this.requestForm) {
      if ((this.requestForm.value.stepI.purchasing_id.length == 0) && ((this.requestForm.value.stepI.reqstatus == 'answered') || (this.requestForm.value.stepI.reqstatus == 'favored'))) disable = true;
    }
    return disable
  }

  private loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  private loadPersons(companyId: number, active?: boolean) {
    if (!companyId || companyId <= 0) {
      this.persons = [];
      return;
    }

    this.personsService.getPersonInCompany(companyId, active).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        // Sortiere die Liste nach dem Nachnamen
        this.persons.sort((a, b) => {
          if (a.lastname < b.lastname) {
            return -1;
          }
          if (a.lastname > b.lastname) {
            return 1;
          }
          return 0;
        });

      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Kunden: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }
}
